import { useCallback, useEffect, useRef } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Label } from 'src/shared/components/ui/label'
import { cn } from 'src/shared/components/ui/utils'
import { useDebouncedFieldUpdate } from 'src/shared/hooks/useDebouncedFieldUpdate'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const TWEAK_CHIPS = [
  { en: 'Move the logo', sv: 'Flytta loggan' },
  { en: 'Wrong color', sv: 'Fel färg' },
  { en: 'A bit smaller', sv: 'Lite mindre' },
  { en: 'A bit bigger', sv: 'Lite större' },
] as const

export const ProofCommentField = ({
  proofId,
  value,
  onSave,
  readOnly = false,
  comingSoon = false,
  hideLabel = false,
  showSuggestions = false,
  autoFocus = false,
}: {
  proofId: string
  value: string
  onSave: (comment: string) => void
  readOnly?: boolean
  comingSoon?: boolean
  hideLabel?: boolean
  showSuggestions?: boolean
  autoFocus?: boolean
}) => {
  const { t } = useLocalization()

  const saveComment = useCallback(
    (comment: string) => {
      onSave(comment.trim() === '' ? '' : comment)
    },
    [onSave]
  )

  const [comment, , handleCommentChange] = useDebouncedFieldUpdate(
    value,
    saveComment
  )
  const commentRef = useRef(comment)
  const savedRef = useRef(value)
  const saveRef = useRef(saveComment)
  commentRef.current = comment
  savedRef.current = value
  saveRef.current = saveComment

  useEffect(() => {
    return () => {
      const latest = commentRef.current
      if (latest !== savedRef.current) {
        saveRef.current(latest)
      }
    }
  }, [])

  const addChip = (label: string) => {
    if (comment.includes(label)) return
    const trimmed = comment.trim()
    if (!trimmed) {
      handleCommentChange(label)
      return
    }
    const prefix = trimmed.endsWith('.') ? trimmed : `${trimmed}.`
    handleCommentChange(`${prefix} ${label}`)
  }

  if (readOnly) {
    if (!value.trim()) return null

    return (
      <Column className="gap-1.5">
        {!hideLabel ? <Label>{t('Note', 'Anteckning')}</Label> : null}
        <p className="whitespace-pre-wrap text-sm text-muted-foreground">
          {value}
        </p>
      </Column>
    )
  }

  if (comingSoon) {
    return (
      <Column className="gap-1.5">
        <Label htmlFor={`proof-comment-${proofId}`}>
          {t('Note', 'Anteckning')}
        </Label>
        <textarea
          id={`proof-comment-${proofId}`}
          value={value}
          disabled
          rows={2}
          placeholder={t(
            'Comments coming soon...',
            'Kommentarer kommer snart...'
          )}
          className={cn(
            'w-full resize-y rounded-xl border border-input bg-muted/40 px-3 py-2 text-sm',
            'placeholder:text-muted-foreground'
          )}
        />
      </Column>
    )
  }

  return (
    <Column className="gap-2">
      {!hideLabel ? (
        <Label htmlFor={`proof-comment-${proofId}`}>
          {t('A note for us', 'En anteckning till oss')}
        </Label>
      ) : null}

      {showSuggestions ? (
        <Row className="flex-wrap gap-1.5">
          {TWEAK_CHIPS.map((chip) => {
            const label = t(chip.en, chip.sv)
            const active = comment.includes(label)
            return (
              <button
                key={chip.en}
                type="button"
                onClick={() => addChip(label)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors',
                  active
                    ? 'border-foreground/20 bg-foreground/[0.06] text-foreground'
                    : 'border-[hsl(var(--border-inner))] bg-white text-muted-foreground hover:border-foreground/20 hover:text-foreground'
                )}
              >
                {label}
              </button>
            )
          })}
        </Row>
      ) : null}

      <textarea
        id={`proof-comment-${proofId}`}
        value={comment}
        onChange={(e) => handleCommentChange(e.target.value)}
        autoFocus={autoFocus}
        placeholder={t(
          'Or type anything we should change…',
          'Eller skriv vad vi ska ändra…'
        )}
        rows={2}
        className={cn(
          'w-full resize-none rounded-xl border border-input bg-white px-3 py-2.5 text-sm leading-relaxed',
          'placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
        )}
      />
    </Column>
  )
}
